import { validateManifestUrl } from "./command-center-manifest.mjs";

export const COMMAND_CENTER_MENU_KEY = "command_center_menu_cache_v1";
export const COMMAND_CENTER_ICON_CACHE = "vector-command-center-icons-v1";
export const COMMAND_CENTER_ICON_PATH = "/_command-center/icon";
const MAX_AGE = 7 * 24 * 60 * 60 * 1000;
const writes = new WeakMap();
const menuCollator = new Intl.Collator("es", { sensitivity: "base" });

function validIconUrl(value) {
  const url = new URL(value);
  validateManifestUrl(url.href.split("#")[0]);
  return url.href;
}

export function emptyCommandCenterMenu(scope = "") {
  return { scope, apps: [], metadata: {}, savedAt: 0 };
}

export function commandCenterMenuScope(origin, group) {
  return JSON.stringify([origin, group]);
}

export function mergeCommandCenterMenu(current, apps, metadata = current.metadata) {
  const nextMetadata = {};
  for (const app of apps) {
    const entry = metadata[app.id];
    if (entry && entry.manifestUrl === app.manifestUrl) nextMetadata[app.id] = entry;
  }
  const sortedApps = [...apps].sort((first, second) =>
    menuCollator.compare(nextMetadata[first.id]?.name || first.id, nextMetadata[second.id]?.name || second.id) ||
    menuCollator.compare(first.id, second.id));
  const unchanged = current.apps.length === sortedApps.length && current.apps.every((app, index) =>
    app.id === sortedApps[index].id && app.manifestUrl === sortedApps[index].manifestUrl);
  const sameMetadata = Object.keys(current.metadata).length === Object.keys(nextMetadata).length &&
    Object.keys(nextMetadata).every((id) => current.metadata[id] === nextMetadata[id]);
  if (unchanged && sameMetadata) return current;
  return { ...current, apps: unchanged ? current.apps : sortedApps, metadata: sameMetadata ? current.metadata : nextMetadata };
}

export function validCommandCenterMenu(snapshot, scope, now = Date.now()) {
  try {
    if (!snapshot || snapshot.version !== 1 || snapshot.scope !== scope ||
        !Number.isFinite(snapshot.savedAt) || snapshot.savedAt > now || now - snapshot.savedAt > MAX_AGE ||
        !Array.isArray(snapshot.apps) || snapshot.apps.length > 100 ||
        !snapshot.metadata || typeof snapshot.metadata !== "object" ||
        JSON.stringify(snapshot).length > 65536) return null;
    const ids = new Set();
    const metadata = {};
    const apps = snapshot.apps.map((app) => {
      if (!app || typeof app.id !== "string" || !/^[a-z0-9][a-z0-9_-]{0,63}$/.test(app.id) || ids.has(app.id)) throw new Error("Invalid ID");
      ids.add(app.id);
      const manifestUrl = validateManifestUrl(app.manifestUrl);
      const entry = snapshot.metadata[app.id];
      if (entry) {
        if (entry.manifestUrl !== manifestUrl || typeof entry.name !== "string" ||
            !entry.name.trim() || entry.name.length > 100) throw new Error("Invalid metadata");
        const iconUrl = validIconUrl(entry.iconUrl);
        let imageUrl = "";
        if (entry.imageUrl) {
          const local = new URL(entry.imageUrl, JSON.parse(scope)[0]);
          if (local.origin !== JSON.parse(scope)[0] || local.pathname !== COMMAND_CENTER_ICON_PATH ||
              local.searchParams.get("url") !== iconUrl) throw new Error("Invalid cached image");
          imageUrl = local.pathname + local.search;
        }
        metadata[app.id] = { name: entry.name, iconUrl, manifestUrl, imageUrl };
      }
      return { id: app.id, manifestUrl };
    });
    return mergeCommandCenterMenu({ ...emptyCommandCenterMenu(scope), savedAt: snapshot.savedAt }, apps, metadata);
  } catch (_error) {
    return null;
  }
}

export async function readCommandCenterMenu(database, scope, now = Date.now()) {
  try {
    await writes.get(database);
    return validCommandCenterMenu(await database.getItem(COMMAND_CENTER_MENU_KEY), scope, now);
  } catch (_error) { return null; }
}

export function saveCommandCenterMenu(database, snapshot, now = Date.now()) {
  const value = snapshot ? { ...snapshot, savedAt: now, version: 1 } : null;
  const write = (writes.get(database) || Promise.resolve()).then(async () => {
    try {
      if (value && validCommandCenterMenu(value, value.scope, now)) await database.setItem(COMMAND_CENTER_MENU_KEY, value);
      else await database.removeItem(COMMAND_CENTER_MENU_KEY);
    } catch (_error) {}
  });
  writes.set(database, write);
  return write;
}

export async function cacheCommandCenterIcon(iconUrl, options = {}) {
  const browser = options.browser || globalThis.window;
  let cache;
  let request;
  try {
    validIconUrl(iconUrl);
    if (!browser?.navigator?.serviceWorker?.controller || !browser.caches || options.signal?.aborted) return "";
    const estimate = await browser.navigator.storage?.estimate?.();
    if (estimate?.quota && estimate.usage + 8 * 1024 * 1024 > estimate.quota * 0.8) return "";
    cache = await browser.caches.open(COMMAND_CENTER_ICON_CACHE);
    if ((await cache.keys()).length >= 200) return "";
    const controller = new AbortController();
    const abort = () => controller.abort();
    options.signal?.addEventListener("abort", abort, { once: true });
    const timer = setTimeout(abort, 8000);
    try {
      const response = await browser.fetch(iconUrl, { mode: "no-cors", credentials: "omit",
        cache: "no-store", redirect: "follow", referrerPolicy: "no-referrer", signal: controller.signal });
      if (options.signal?.aborted || (response.type !== "opaque" && !response.ok)) return "";
      const local = new URL(COMMAND_CENTER_ICON_PATH, browser.location.origin);
      local.searchParams.set("url", iconUrl);
      local.searchParams.set("v", browser.crypto.randomUUID());
      request = new Request(local.href, { mode: "no-cors", credentials: "omit" });
      await cache.put(request, response);
      await new Promise((resolve, reject) => {
        const image = new browser.Image();
        const stop = () => { image.src = ""; reject(new Error("Canceled image")); };
        const finish = (error) => {
          controller.signal.removeEventListener("abort", stop);
          image.onload = null;
          image.onerror = null;
          if (error) reject(error); else resolve();
        };
        image.referrerPolicy = "no-referrer";
        image.onload = () => finish();
        image.onerror = () => finish(new Error("Invalid image"));
        controller.signal.addEventListener("abort", stop, { once: true });
        if (controller.signal.aborted) stop();
        else image.src = local.href;
      });
      if (options.signal?.aborted) throw new Error("Canceled image");
      return local.pathname + local.search;
    } finally {
      clearTimeout(timer);
      options.signal?.removeEventListener("abort", abort);
    }
  } catch (_error) {
    if (cache && request) await cache.delete(request).catch(() => {});
    return "";
  }
}

export async function pruneCommandCenterIcons(imageUrls, browser = globalThis.window) {
  try {
    if (!browser?.caches) return;
    const cache = await browser.caches.open(COMMAND_CENTER_ICON_CACHE);
    const keep = new Set(imageUrls.filter(Boolean).map((url) => new URL(url, browser.location.origin).href));
    await Promise.all((await cache.keys()).filter((request) => !keep.has(request.url)).map((request) => cache.delete(request)));
  } catch (_error) {}
}